// ARCHIVO COMPARTIDO app/web — editar en ambos repos. Verificar con `npm run check:shared` (TemControlApp).
//
// ── Histórico de horarios (ScheduleVersion) por fecha ─────────────────────
// Parte PURA de la preparación del horario de un trabajador: a partir de las
// respuestas crudas del backend (workhours actuales + histórico de versiones)
// construye lo que consumen la bolsa de horas, los contadores de vacaciones /
// días completos, las horas trabajadas de la ficha y el "descanso" de los
// calendarios. Antes cada pantalla lo montaba a su manera (la ficha de la
// web usaba el histórico, la de la app y los calendarios solo el horario
// actual) y el mismo día contaba distinto según dónde se mirase.
//
// Las llamadas a la API se quedan en el código de cada repo: aquí solo se
// recibe una función `fetchWorkHoursAtDate(isoDate)` ya ligada al trabajador.

import { makeSlotResolver } from './timeline';
import { buildWorkHoursByDow } from './vacationCounter';
import { DEFAULT_TIME_ZONE } from './workerTime';

// ¿La versión trae sus slots reales?
export const versionHasSlots = (v) => Array.isArray(v?.assignSlots) && v.assignSlots.length > 0;

// Rellena los slots de cada versión del histórico. Workaround: GET
// /api/scheduleVersion/history no siempre devuelve los assignSlots, pero
// GET /api/workhours/:id/date/:validFrom SÍ devuelve los slots vigentes en
// esa fecha. Best-effort: si una petición falla, la versión se queda sin
// slots y buildEnrichedScheduleHistory la rellena con el horario actual.
//   `fetchWorkHoursAtDate(iso)` → Promise<{ workhours: slot[] }>.
export const enrichScheduleVersions = async (versions, fetchWorkHoursAtDate) => {
  const list = Array.isArray(versions) ? versions : [];
  return Promise.all(list.map(async (v) => {
    if (!v || versionHasSlots(v) || !v.validFrom || typeof fetchWorkHoursAtDate !== 'function') return v;
    try {
      const r = await fetchWorkHoursAtDate(String(v.validFrom).slice(0, 10));
      return Array.isArray(r?.workhours) && r.workhours.length > 0 ? { ...v, assignSlots: r.workhours } : v;
    } catch {
      return v;
    }
  }));
};

// Workaround para dos limitaciones del backend / del modelo de datos:
//   1. Versiones sin assignSlots → se rellenan con el workhours actual.
//   2. Fechas ANTERIORES a la primera ScheduleVersion → se añade una
//      "versión virtual" al FINAL que cubre cualquier fecha no cubierta por
//      las reales (findActiveEntry itera en orden y la real gana).
// Sin workhours actuales se devuelve el histórico tal cual.
export const buildEnrichedScheduleHistory = (scheduleHistory, workhours) => {
  const wh = Array.isArray(workhours) ? workhours : [];
  const hist = Array.isArray(scheduleHistory) ? scheduleHistory : [];
  if (wh.length === 0) return hist;
  const real = hist.map((v) => (versionHasSlots(v) ? v : { ...v, assignSlots: wh }));
  const virtual = { id: -1, validFrom: '1970-01-01', validTo: null, assignSlots: wh };
  return [...real, virtual];
};

// Horario que se le pasa a computeAutoBalance: el histórico enriquecido si
// existe; si no, los slots actuales (array plano legacy).
export const balanceScheduleInput = (enrichedScheduleHistory, workhours) =>
  (Array.isArray(enrichedScheduleHistory) && enrichedScheduleHistory.length > 0)
    ? enrichedScheduleHistory
    : (Array.isArray(workhours) ? workhours : []);

// Horario para todo lo que se resuelve DÍA A DÍA fuera de la bolsa
// (contadores de vacaciones / días completos, descanso del calendario; las
// pausas ya no dependen del horario: todas se restan): el histórico enriquecido si existe; si no,
// Map<dow, slot> con el horario actual. Mismo fallback en todas partes.
export const scheduleByDateInput = (enrichedScheduleHistory, workhours) =>
  (Array.isArray(enrichedScheduleHistory) && enrichedScheduleHistory.length > 0)
    ? enrichedScheduleHistory
    : buildWorkHoursByDow(workhours);

// `(iso) => slot | null` con el horario vigente en cada fecha. `timeZone`
// (zona del trabajador) solo importa si se le pasa un instante en vez de
// una clave 'YYYY-MM-DD'.
export const makeScheduleResolver = (enrichedScheduleHistory, workhours, timeZone = DEFAULT_TIME_ZONE) =>
  makeSlotResolver(scheduleByDateInput(enrichedScheduleHistory, workhours), timeZone);

// Regla ÚNICA de "día de descanso" de los calendarios (personal web/app y
// comparación): no hay slot configurado ese día en el horario vigente en esa
// fecha. Sin horario ninguno → todos los días son descanso (no se inventa
// un fin de semana). `resolveSlot` sale de makeScheduleResolver.
export const isRestDayAt = (resolveSlot, iso) =>
  !(typeof resolveSlot === 'function' && resolveSlot(iso));
